/**
 * Bibliothèque administrative.
 *
 * Cette page existe pour une raison précise : permettre de vérifier soi-même
 * d'où vient chaque obligation affichée, et de distinguer ce qui est confirmé
 * par une source officielle de ce qui reste à vérifier.
 */

import { useState } from "react";

import { miscApi } from "../api/endpoints";
import { useToast } from "../components/ToastContext";
import {
  Alert,
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorAlert,
  LoadingBlock,
  ObligationBadge,
  PageHeader,
  SourceBox,
  Stat,
} from "../components/ui";
import { useDocumentTitle, useMutation, useResource } from "../hooks/useApi";
import { formatDate } from "../lib/format";

type Filter = "all" | "mine" | "verified" | "to_verify";

export function RulesPage() {
  useDocumentTitle("Bibliothèque administrative");
  const toast = useToast();
  const [filter, setFilter] = useState<Filter>("mine");

  const library = useResource(() => miscApi.rules(), []);

  const sync = useMutation(() => miscApi.syncRules(), {
    onSuccess: async (response) => {
      await library.reload();
      toast.success("Bibliothèque rechargée", response.message);
    },
    onError: (error) => toast.error("Rechargement impossible", error.message),
  });

  if (library.loading) return <LoadingBlock />;
  if (library.error) {
    return <ErrorAlert error={library.error} title="Bibliothèque indisponible" />;
  }
  const data = library.data;
  if (!data) return null;

  const rules = data.rules.filter((rule) => {
    switch (filter) {
      case "mine":
        return (
          rule.applies_to_current_business === true ||
          rule.applicability_status === "undetermined"
        );
      case "verified":
        return rule.verification_status === "verified";
      case "to_verify":
        return rule.verification_status === "needs_verification";
      default:
        return true;
    }
  });

  return (
    <>
      <PageHeader
        title="Bibliothèque administrative"
        subtitle="Chaque règle porte sa source officielle, sa date de vérification et son statut."
        actions={
          <Button
            variant="ghost"
            loading={sync.pending}
            onClick={() => void sync.run()}
            title="Recharge les règles installées. Vos démarches existantes ne sont pas modifiées."
          >
            Recharger la bibliothèque
          </Button>
        }
      />

      <div className="stack">
        <Alert tone="info" title="Ce que l'application s'engage à ne pas faire">
          Aucune obligation, échéance ou taux n'est inventé. Lorsqu'une règle n'a
          pas pu être confirmée sur une source officielle, elle est présentée comme
          une <strong>donnée à vérifier</strong> et non comme une obligation
          certaine. De même, une échéance estimée est signalée comme telle.
        </Alert>

        <section className="grid grid--3" aria-label="Chiffres de la bibliothèque">
          <Stat value={data.rules_count} label="Règles installées" />
          <Stat
            value={data.verified_count}
            label="Vérifiées sur source officielle"
            tone="success"
          />
          <Stat
            value={data.needs_verification_count}
            label="À vérifier avant de s'y fier"
            tone={data.needs_verification_count > 0 ? "warning" : undefined}
          />
        </section>

        <div className="chips" role="group" aria-label="Filtrer les règles">
          <Chip
            label="Celles qui me concernent"
            pressed={filter === "mine"}
            onToggle={() => setFilter("mine")}
          />
          <Chip
            label="Toutes"
            pressed={filter === "all"}
            onToggle={() => setFilter("all")}
          />
          <Chip
            label="Vérifiées"
            pressed={filter === "verified"}
            onToggle={() => setFilter("verified")}
          />
          <Chip
            label="À vérifier"
            pressed={filter === "to_verify"}
            onToggle={() => setFilter("to_verify")}
          />
        </div>

        {rules.length === 0 ? (
          <Card>
            <EmptyState title="Aucune règle ne correspond à ce filtre" />
          </Card>
        ) : (
          <div className="stack">
            {rules.map((rule) => (
              <Card
                key={`${rule.rule_key}-${rule.version}`}
                title={rule.title}
                hint={rule.summary ?? undefined}
                actions={
                  <>
                    <ObligationBadge
                      obligation={rule.obligation}
                      label={rule.obligation_label}
                    />
                    {rule.verification_status === "verified" ? (
                      <span className="badge badge--success">Source vérifiée</span>
                    ) : (
                      <span className="badge badge--warning">Donnée à vérifier</span>
                    )}
                  </>
                }
              >
                <div className="row" style={{ marginBottom: "0.75rem" }}>
                  <span className="badge">{rule.category_label}</span>
                  {rule.authority && <span className="badge">{rule.authority}</span>}
                  <span className="badge">{rule.recurrence}</span>
                  {rule.applicability_status === "applicable" && (
                    <span className="badge badge--accent">Vous concerne</span>
                  )}
                  {rule.applicability_status === "undetermined" && (
                    <span className="badge badge--warning">
                      Information manquante
                    </span>
                  )}
                  {rule.applicability_status === "not_applicable" && (
                    <span className="badge">Ne vous concerne pas</span>
                  )}
                </div>

                {rule.applicability_explanation && (
                  <p>{rule.applicability_explanation}</p>
                )}

                {rule.deadline_note && (
                  <p className="inline-note">
                    <strong>Échéance : </strong>
                    {rule.deadline_note}
                  </p>
                )}

                <SourceBox
                  name={rule.source_name}
                  url={rule.source_url}
                  checkedOn={
                    rule.source_checked_on ? formatDate(rule.source_checked_on) : null
                  }
                  quote={rule.source_quote}
                  note={rule.verification_note}
                />

                <p className="inline-note mt-3 mb-0">
                  Identifiant : <span className="mono">{rule.rule_key}</span> — version{" "}
                  {rule.version}
                </p>
              </Card>
            ))}
          </div>
        )}

        {data.notice && (
          <Card title="Note de la bibliothèque">
            <p className="text-muted mb-0">{data.notice}</p>
            {data.library_version && (
              <p className="inline-note mt-3 mb-0">
                Version installée : {data.library_version}.
              </p>
            )}
          </Card>
        )}
      </div>
    </>
  );
}
